import React from "react";
import { useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import FilterSearch from "pages/parts/FilterSearch";
import { DOWNLOAD_FILE, GET } from "helpers/api";
import { findFromArrayById, formatDate, formatDateBackend, formatMoney, returnSign } from "helpers/helpers";
import { Link } from 'react-router-dom';


const initialChartData = {
  salesByPos: { categories: [], series: [] },
  payments: { total: 0, items: [] },
  cashiers: { categories: [], sales: [], profit: [] },
  suppliers: { categories: [], sales: [], profit: [] },
  categories: { categories: [], data: [] },
  topProducts: { categories: [], data: [] },
  profitProducts: { categories: [], data: [] },
};

const paymentColors = ["#29b37e", "#ff9f1c", "#168de2", "#cbd5e1"];

function NewSalesGraphic() {
  const { t } = useTranslation();
  const date = new Date();
  const reduxSettings = useSelector(state => state.settings);
  const reduxAccount = useSelector(state => state.account);
  const currencies = [{ id: 1, name: "So`m" }, { id: 2, name: "USD" }];

  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [filterDataExist, setFilterDataExist] = useState(false);
  const [poses, setPoses] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalAmount, setTotalAmount] = useState([]);
  const [chartData, setChartData] = useState(initialChartData);
  const [filterData, setFilterData] = useState({
    startDate: formatDateBackend(new Date(date.getFullYear(), date.getMonth(), 1)),
    endDate: formatDateBackend(new Date()),
    posId: reduxAccount.posId,
    organizationId: null,
    productCategoryId: null,
    cashierId: null,
    paymentTypeId: null,
    currencyId: reduxSettings?.currencyId ? reduxSettings.currencyId : 1,
    search: "",
  });

  function requestParams(source = filterData) {
    const params = {};
    if (source.startDate) params.startDate = source.startDate;
    if (source.endDate) params.endDate = source.endDate;
    if (source.posId) params.posId = source.posId;
    if (source.organizationId) params.organizationId = source.organizationId;
    if (source.productCategoryId) params.productCategoryId = source.productCategoryId;
    if (source.cashierId) params.cashierId = source.cashierId;
    if (source.paymentTypeId) params.paymentTypeId = source.paymentTypeId;
    if (source.currencyId) params.currencyId = source.currencyId;
    if (source.search) params.search = source.search;
    return params;
  }

  function dashboardParams(source = filterData, includeCurrency = true) {
    const params = {};
    if (source.startDate) params.startDate = source.startDate;
    if (source.endDate) params.endDate = source.endDate;
    if (source.posId) params.posId = source.posId;
    if (includeCurrency && source.currencyId) params.currencyId = source.currencyId;
    return params;
  }

  function totalUrl(source = filterData) {
    let urlParams = "";
    const params = requestParams(source);
    Object.keys(params).forEach(key => {
      urlParams += returnSign(urlParams) + key + "=" + params[key];
    });
    return "/services/web/api/report-sales-total" + urlParams;
  }

  function mapPaymentData(data) {
    if (!data) return initialChartData.payments;
    const items = [
      { name: t("CASH"), y: Number(data.cash || 0) },
      { name: t("TERMINAL"), y: Number(data.terminal || 0) },
      { name: t("DEBT"), y: Number(data.debt || 0) },
      { name: t("OTHER"), y: Number(data.others || 0) },
    ].filter(item => item.y > 0);

    return {
      total: items.reduce((sum, item) => sum + item.y, 0),
      items,
    };
  }

  function mapSalesByPosData(data) {
    const rows = Array.isArray(data) ? data : [];
    const uniquePoints = [];
    const seenKeys = new Set();

    rows.forEach(item => {
      if (Array.isArray(item.points)) {
        item.points.forEach(point => {
          const key = point.date || point.label;
          if (key && !seenKeys.has(key)) {
            seenKeys.add(key);
            uniquePoints.push({
              key,
              date: point.date,
              label: point.label || (point.date ? formatDate(point.date).slice(0, 5) : "")
            });
          }
        });
      }
    });

    uniquePoints.sort((a, b) => {
      if (a.date && b.date) {
        return a.date.localeCompare(b.date);
      }
      return a.key.localeCompare(b.key);
    });

    return {
      categories: uniquePoints.map(p => p.label),
      series: rows.map(item => {
        const pointDataMap = {};
        if (Array.isArray(item.points)) {
          item.points.forEach(point => {
            const key = point.date || point.label;
            pointDataMap[key] = Number(point.sum || 0);
          });
        }

        return {
          name: item.posName || t("POS"),
          data: uniquePoints.map(p => pointDataMap[p.key] || 0),
        };
      }),
    };
  }

  function mapOrganizationData(data) {
    const rows = Array.isArray(data) ? data.slice(0, 10) : [];
    return {
      categories: rows.map(item => item.organizationName || "-"),
      sales: rows.map(item => Number(item.totalSalePrice || 0)),
      profit: rows.map(item => Number(item.totalProfitPrice || 0)),
    };
  }

  function mapCashierData(data) {
    return {
      categories: Array.isArray(data?.cashiers) ? data.cashiers : [],
      sales: Array.isArray(data?.sales) ? data.sales.map(value => Number(value || 0)) : [],
      profit: Array.isArray(data?.profits) ? data.profits.map(value => Number(value || 0)) : [],
    };
  }

  function mapCategoryData(data) {
    return {
      categories: Array.isArray(data?.categories) ? data.categories : [],
      data: Array.isArray(data?.sales) ? data.sales.map(value => Number(value || 0)) : [],
    };
  }

  function mapProductBalanceData(data) {
    const rows = Array.isArray(data) ? data.slice(0, 10) : [];
    return {
      categories: rows.map(item => item.productName || "-"),
      data: rows.map(item => Number(item.quantity || 0)),
    };
  }

  function mapProfitProductData(data) {
    const rows = Array.isArray(data) ? data.slice(0, 10) : [];
    return {
      categories: rows.map(item => item.productName || "-"),
      data: rows.map(item => Number(item.totalSale || 0)),
    };
  }

  async function searchWithFilter(exportToExcel = false, source = filterData) {
    const params = requestParams(source);
    setFilterDataExist(Object.keys(params).some(key => key !== "currencyId" && params[key]));

    if (exportToExcel) {
      await DOWNLOAD_FILE("/services/web/api/report-sales-excel", t("SALES_REPORT"), params);
      return;
    }

    const [
      salesByPosResponse,
      paymentResponse,
      cashierResponse,
      organizationResponse,
      categoryResponse,
      productBalanceResponse,
      profitProductResponse,
    ] = await Promise.all([
      GET("/services/web/api/report-sales-by-pos", dashboardParams(source, true)),
      GET("/services/web/api/report-sales-by-payment", dashboardParams(source, true)),
      GET("/services/web/api/report-sales-and-profit-by-cashiers", dashboardParams(source, true)),
      GET("/services/web/api/report-sales-by-organization", dashboardParams(source, true)),
      GET("/services/web/api/report-sales-by-categories", dashboardParams(source, true)),
      GET("/services/web/api/report-sales-top-product-by-quantity", dashboardParams(source, false)),
      GET("/services/web/api/report-sales-top-product", dashboardParams(source, true)),
    ]);

    setChartData(prev => ({
      ...prev,
      salesByPos: mapSalesByPosData(salesByPosResponse),
      payments: mapPaymentData(paymentResponse),
      cashiers: mapCashierData(cashierResponse),
      suppliers: mapOrganizationData(organizationResponse),
      categories: mapCategoryData(categoryResponse),
      topProducts: mapProductBalanceData(productBalanceResponse),
      profitProducts: mapProfitProductData(profitProductResponse),
    }));

    const totalResponse = await GET(totalUrl(source));
    if (totalResponse) setTotalAmount(totalResponse);
    setDropdownIsOpen(false);
  }

  function resetFilter() {
    const resetData = {
      startDate: null,
      endDate: null,
      posId: null,
      organizationId: null,
      productCategoryId: null,
      cashierId: null,
      paymentTypeId: null,
      currencyId: reduxSettings?.currencyId ? reduxSettings.currencyId : 1,
      search: "",
    };
    setFilterData(resetData);
    setFilterDataExist(false);
    setChartData(initialChartData);
    searchWithFilter(false, resetData);
  }

  useEffect(() => {
    async function loadHelpers() {
      const [posRes, cashierRes, paymentRes, orgRes, categoryRes] = await Promise.all([
        GET("/services/web/api/pos-helper"),
        GET("/services/web/api/cashier-helper"),
        GET("/services/web/api/payment-type-helper"),
        GET("/services/web/api/organization-helper"),
        GET("/services/web/api/product-category-helper"),
      ]);

      if (posRes) setPoses([{ id: null, name: t("ALL") }, ...posRes]);
      if (cashierRes) setCashiers(cashierRes);
      if (paymentRes) setPaymentTypes(paymentRes);
      if (orgRes) setOrganizations(orgRes);
      if (categoryRes) setCategories(categoryRes);
    }

    loadHelpers();
    searchWithFilter(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const moneySuffix = " " + (findFromArrayById(currencies, filterData.currencyId) || "");
  const baseOptions = {
    credits: { enabled: false },
    colors: ["#168de2", "#29b37e", "#ff9f1c", "#6c757d", "#15aabf"],
    legend: { itemStyle: { fontSize: "11px" } },
    title: { style: { fontSize: "13px", fontWeight: "600" } },
    yAxis: {
      title: { text: "" },
      labels: {
        formatter: function () {
          return formatMoney(this.value);
        },
      },
    },
    tooltip: { shared: true, valueDecimals: 0, valueSuffix: moneySuffix },
  };

  const salesByPosOptions = useMemo(() => ({
    ...baseOptions,
    chart: { type: "line", height: 310 },
    title: { text: t("SALES_REPORT") + " - " + t("POS"), style: baseOptions.title.style },
    xAxis: { categories: chartData.salesByPos.categories },
    series: chartData.salesByPos.series,
  }), [chartData.salesByPos, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const paymentOptions = useMemo(() => ({
    ...baseOptions,

    chart: {
      type: "pie",
      height: 220,
      spacing: [0, 0, 0, 0],
      margin: [0, 0, 0, 0],
      backgroundColor: "transparent",
    },

    colors: paymentColors,

    title: {
      text: undefined,
    },

    subtitle: {
      text: undefined,
    },

    legend: {
      enabled: false,
    },

    plotOptions: {
      pie: {
        center: ["50%", "50%"],
        size: "95%",
        innerSize: "70%",
        borderWidth: 0,
        dataLabels: {
          enabled: false,
        },
        states: {
          hover: {
            enabled: chartData.payments.items.length > 0,
            halo: {
              size: 0,
            },
          },
        },
      },
    },

    tooltip: {
      enabled: chartData.payments.items.length > 0,
      pointFormatter: function () {
        return `<b>${formatMoney(this.y)}</b> (${this.percentage.toFixed(1)}%)`;
      },
    },

    series: [
      {
        name: t("TOTAL_AMOUNT"),
        data: chartData.payments.items.length > 0
          ? chartData.payments.items
          : [{ name: "", y: 1, color: "#e2e8f0" }],
      },
    ],
  }), [chartData.payments, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const cashierOptions = useMemo(() => ({
    ...baseOptions,
    chart: { type: "column", height: 310 },
    title: { text: t("CASHIER"), style: baseOptions.title.style },
    xAxis: { categories: chartData.cashiers.categories },
    series: [
      { name: t("SALES_REPORT"), data: chartData.cashiers.sales },
      { name: t("GROSS_INCOME"), data: chartData.cashiers.profit },
    ],
  }), [chartData.cashiers, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const supplierOptions = useMemo(() => ({
    ...baseOptions,
    chart: { type: "column", height: 310 },
    title: { text: "TOP-10 " + t("SUPPLIERS"), style: baseOptions.title.style },
    xAxis: { categories: chartData.suppliers.categories },
    series: [
      { name: t("SALES_REPORT"), data: chartData.suppliers.sales },
      { name: t("GROSS_INCOME"), data: chartData.suppliers.profit },
    ],
  }), [chartData.suppliers, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryOptions = useMemo(() => ({
    ...baseOptions,
    chart: { type: "column", height: 310 },
    title: { text: t("CATEGORY"), style: baseOptions.title.style },
    xAxis: { categories: chartData.categories.categories, labels: { rotation: -45 } },
    series: [{ name: t("TOTAL_AMOUNT"), data: chartData.categories.data }],
  }), [chartData.categories, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const productOptions = useMemo(() => ({
    ...baseOptions,
    chart: { type: "bar", height: 406 },
    title: { text: "TOP-10 " + t("NAME_OF_PRODUCT"), style: baseOptions.title.style },
    xAxis: { categories: chartData.topProducts.categories },
    tooltip: { shared: true, valueDecimals: 0, valueSuffix: " шт" },
    series: [{ name: t("QUANTITY"), data: chartData.topProducts.data, color: "#29b37e" }],
  }), [chartData.topProducts, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const profitProductOptions = useMemo(() => ({
    ...baseOptions,
    chart: { type: "bar", height: 406 },
    title: { text: "TOP-10 " + t("GROSS_INCOME"), style: baseOptions.title.style },
    xAxis: { categories: chartData.profitProducts.categories },
    series: [{ name: t("GROSS_INCOME"), data: chartData.profitProducts.data }],
  }), [chartData.profitProducts, t]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="page-header d-flex justify-content-between mb-2">
        <h4 className="vertical-center">{t("SALES_REPORT")}</h4>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between flex-wrap mb-3">
            <div className="d-flex flex-wrap">
              <div>
                <FilterSearch filterData={filterData} setFilterData={setFilterData} />
              </div>

              <div>
                <Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
                  <Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
                    {t("FILTER")}
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="dropdown-filter">
                    <div className="row">
                      <div className="form-group col-md-6">
                        <label>{t("PERIOD")}</label>
                        <DatePicker
                          className="form-control"
                          dateFormat="dd.MM.yyyy"
                          selected={filterData.startDate ? new Date(filterData.startDate) : null}
                          onChange={value => setFilterData({ ...filterData, startDate: value ? formatDateBackend(value) : null })}
                        />
                      </div>
                      <div className="form-group col-md-6">
                        <label>{t(".")}</label>
                        <DatePicker
                          className="form-control"
                          dateFormat="dd.MM.yyyy"
                          selected={filterData.endDate ? new Date(filterData.endDate) : null}
                          onChange={value => setFilterData({ ...filterData, endDate: value ? formatDateBackend(value) : null })}
                        />
                      </div>
                      <FilterSelect label={t("POS")} options={poses} value={filterData.posId} onChange={value => setFilterData({ ...filterData, posId: value })} t={t} />
                      <FilterSelect label={t("SUPPLIER")} options={organizations} value={filterData.organizationId} onChange={value => setFilterData({ ...filterData, organizationId: value })} t={t} clearable />
                      <FilterSelect label={t("CASHIER")} options={cashiers} value={filterData.cashierId} onChange={value => setFilterData({ ...filterData, cashierId: value })} t={t} clearable />
                      <FilterSelect label={t("PAYMENT_METHOD")} options={paymentTypes} value={filterData.paymentTypeId} onChange={value => setFilterData({ ...filterData, paymentTypeId: value })} t={t} clearable />
                      <FilterSelect label={t("CATEGORY")} options={categories} value={filterData.productCategoryId} onChange={value => setFilterData({ ...filterData, productCategoryId: value })} t={t} clearable />
                      <FilterSelect label={t("CURRENCY")} options={currencies} value={filterData.currencyId} onChange={value => setFilterData({ ...filterData, currencyId: value })} t={t} />
                    </div>
                    <div className="row mt-3">
                      <div className="col-md-12">
                        <div className="d-flex justify-content-end">
                          <button className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t("CANCEL")}</button>
                          <button className="btn btn-primary" onClick={() => searchWithFilter(false)}>{t("APPLY")}</button>
                        </div>
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div>
                <button className="me-2 btn btn-outline-primary btn-wide" onClick={() => searchWithFilter(true)}>EXCEL</button>
              </div>
              <div>
                <Link to={"/report/sales"}><button className="btn btn-primary btn-wide" onClick={() => searchWithFilter(false)}>GRAFIK</button></Link>
              </div>
            </div>

            <div>
              <TotalRows totalAmount={totalAmount} t={t} />
            </div>
          </div>

          {filterDataExist && (
            <div className="filter-block mb-3">
              <div className="filter">
                <strong>{t("FILTER")}</strong><br />
                {(filterData.startDate || filterData.endDate) && (
                  <span className="me-2">
                    <b>{t("PERIOD")}: </b>
                    {filterData.startDate && <span>{formatDate(filterData.startDate)}</span>}
                    {(filterData.startDate && filterData.endDate) && <span>|</span>}
                    {filterData.endDate && <span>{formatDate(filterData.endDate)}</span>}
                  </span>
                )}
                {filterData.posId && <FilterText label={t("POS")} value={findFromArrayById(poses, filterData.posId)} />}
                {filterData.cashierId && <FilterText label={t("CASHIER")} value={findFromArrayById(cashiers, filterData.cashierId)} />}
                {filterData.organizationId && <FilterText label={t("SUPPLIER")} value={findFromArrayById(organizations, filterData.organizationId)} />}
                {filterData.productCategoryId && <FilterText label={t("CATEGORY")} value={findFromArrayById(categories, filterData.productCategoryId)} />}
                {filterData.paymentTypeId && <FilterText label={t("PAYMENT_METHOD")} value={findFromArrayById(paymentTypes, filterData.paymentTypeId)} />}
                {filterData.currencyId && <FilterText label={t("CURRENCY")} value={findFromArrayById(currencies, filterData.currencyId)} />}
                {filterData.search && <FilterText label={t("SEARCH")} value={filterData.search} />}
              </div>
              <div className="vertical-center">
                <button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t("RESET")}</button>
              </div>
            </div>
          )}

          <div className="row g-2">
            <ChartBlock className="col-12" height={310} options={salesByPosOptions} />
            <ChartBlock className="col-lg-6" height={310}>
              <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "10px 15px", justifyContent: "space-between" }}>
                <div style={{
                  textAlign: "center",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#333",
                  marginBottom: "8px"
                }}>
                  {t("PAYMENT_METHOD")}
                </div>
                
                <div style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flex: 1,
                  minHeight: 0
                }}>
                  {/* Left: Donut Chart + Center Text */}
                  <div style={{
                    position: "relative",
                    width: "48%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <div style={{ width: "100%", height: "220px" }}>
                      <HighchartsReact highcharts={Highcharts} options={paymentOptions} />
                    </div>
                    
                    {/* Center Text Overlay */}
                    <div style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      pointerEvents: "none",
                      zIndex: 10
                    }}>
                      <div style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#1e293b",
                        lineHeight: "1.2"
                      }}>
                        {formatMoney(chartData.payments.total, 0)}
                      </div>
                      <div style={{
                        fontSize: "12px",
                        color: "#64748b",
                        marginTop: "2px",
                        fontWeight: "500"
                      }}>
                        {findFromArrayById(currencies, filterData.currencyId) || ""}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right: Legend */}
                  <div style={{
                    width: "52%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    paddingLeft: "12px"
                  }}>
                    {chartData.payments.items.map((item, index) => {
                      const percentage = chartData.payments.total > 0
                        ? ((item.y / chartData.payments.total) * 100).toFixed(1)
                        : "0.0";
                      const color = paymentColors[index % paymentColors.length];
                      return (
                        <div key={index} style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "10px",
                          fontSize: "11px",
                          lineHeight: "1.4"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", minWidth: 0, marginRight: "8px" }}>
                            <span style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: color,
                              marginRight: "6px",
                              flexShrink: 0
                            }} />
                            <span style={{
                              color: "#64748b",
                              fontWeight: "500",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}>
                              {item.name}
                            </span>
                          </div>
                          <span style={{
                            color: "#1e293b",
                            fontWeight: "600",
                            whiteSpace: "nowrap"
                          }}>
                            {formatMoney(item.y)} ({percentage}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </ChartBlock>
            <ChartBlock className=" col-lg-6" height={310} options={cashierOptions} />
            <ChartBlock className="col-xl-8 col-lg-7" height={310} options={supplierOptions} />
            <ChartBlock className="col-xl-4 col-lg-5" height={310} options={categoryOptions} />
            <ChartBlock className="col-lg-6" height={406} options={productOptions} />
            <ChartBlock className="col-lg-6" height={406} options={profitProductOptions} />
          </div>
        </div>
      </div>
    </>
  );
}

function FilterSelect({ label, options, value, onChange, t, clearable = false }) {
  return (
    <div className="form-group col-md-6">
      <label>{label}</label>
      <Select
        isClearable={clearable}
        options={options}
        value={options.find(option => option.id === value) || ""}
        onChange={option => onChange(option ? option.id : null)}
        placeholder=""
        noOptionsMessage={() => t("LIST_IS_EMPTY")}
        getOptionLabel={option => option.name}
        getOptionValue={option => option.id}
      />
    </div>
  );
}

function FilterText({ label, value }) {
  return <span className="me-2"><b>{label}: </b>{value}</span>;
}

function TotalRows({ totalAmount, t }) {
  return (
    <>
      <div className="d-flex justify-content-between">
        <div className="vertical-center me-2"><h4><b>{t("TOTAL_AMOUNT")}</b>:</h4></div>
        <div className="vertical-center">
          {totalAmount?.map((item, index) => (
            <h5 className="text-end" key={index}>
              <span className="me-1">{formatMoney(item.totalAmount)}</span>
              <b>{item.currencyName}</b>
            </h5>
          ))}
        </div>
      </div>
      <div className="d-flex justify-content-between">
        <div className="vertical-center me-2"><h4><b>{t("TOTAL_AMOUNT_RETURN")}</b>:</h4></div>
        <div className="vertical-center">
          {totalAmount?.map((item, index) => (
            <h5 className="text-end" key={index}>
              <span className="me-1">{formatMoney(item.returnAmount)}</span>
              <b>{item.currencyName}</b>
            </h5>
          ))}
        </div>
      </div>
    </>
  );
}

function ChartBlock({ className, height, options, children }) {
  return (
    <div className={className + " mb-3"}>
      <div className="card h-100" style={{ minHeight: height }}>
        <div className="card-body p-2">
          {children ? children : <HighchartsReact highcharts={Highcharts} options={options} />}
        </div>
      </div>
    </div>
  );
}

export default NewSalesGraphic;
